import { useState, useCallback } from "react";
import { useKeycloak } from "@react-keycloak/web";
import { useEnv } from "@RHCommerceDev/hooks/useEnv";
import { processEnvServer } from "@RHCommerceDev/hooks/useSsrHooks";
import { useCookiesWithPermission } from "@RHCommerceDev/hooks/useCookiesWithPermission";
import useUserPreferences from "@RHCommerceDev/hooks/useUserPreferences";
import useBrand from "@RHCommerceDev/hooks-use-brand/useBrand";
import useIsoRedirect from "@RHCommerceDev/hooks-use-isoredirect";
import { useStytchLogout } from "./useStytchLogout";

export const useAuth = () => {
  // move loading to useSession after remove fetch request in signIn
  const [loading, setLoading] = useState(false);
  const { language } = useUserPreferences();
  const [signedOut, setSignedOut] = useState(false);
  const [error, setError] = useState(false);
  const { setStorageValueWrapper } = useCookiesWithPermission();
  const brand = useBrand();

  let locale = language?.split("-")?.[0] || "en";

  const { keycloak } = useKeycloak();
  const { stytchLogout } = useStytchLogout();

  const {
    REACT_APP_KEYCLOAK_ORIGIN,
    REACT_APP_KEYCLOAK_PATH,
    REACT_APP_KEYCLOAK_REALM,
    REACT_APP_KEYCLOAK_CLIENTID
  } = useEnv();

  const signOut = useCallback(async () => {
    setLoading(true);
    setError(false);

    try {
      // -- TODO: Remove fetch after SessionProvider is fully released
      await stytchLogout();
      setSignedOut(true);
      localStorage.removeItem("isLoggedin");
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);

      // Log the current URL after sign-out is completed
      const currentUrl = window.location.href;
      useIsoRedirect(`${currentUrl}`, "windowAssign");
    }
  }, [
    stytchLogout,
    REACT_APP_KEYCLOAK_ORIGIN,
    REACT_APP_KEYCLOAK_PATH,
    REACT_APP_KEYCLOAK_REALM,
    keycloak?.token,
    brand
  ]);

  const getRedirectUri = useCallback((path?: string) => {
    if (!path) {
      return undefined;
    }

    const url = new URL(window.location.href);
    url.pathname = path;
    return url.href;
  }, []);

  const signIn = useCallback(
    async (path?: string) => {
      if (!processEnvServer) {
        setStorageValueWrapper({
          storageKey: "REACT_APP_KEYCLOAK_CLIENTID",
          value: REACT_APP_KEYCLOAK_CLIENTID
        });
      }

      const redirectUri = getRedirectUri(path);
      keycloak.clientId = REACT_APP_KEYCLOAK_CLIENTID;
      keycloak.login({ redirectUri, locale });
      setSignedOut(false);
    },
    [setStorageValueWrapper]
  );

  return {
    loading,
    error,
    signOut,
    signIn,
    signedOut
  };
};
